import { create } from "zustand";
import {
  ListInventoryLinesResponseSchema,
  type HydratedInventoryLine,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useAuthStore } from "@/store/authStore";

interface InventoryStore {
  lines: Map<string, HydratedInventoryLine>;
  loadingByItemId: Record<string, { isLoading: boolean; error: string | null }>;
  fetchLines: (params: {
    itemId: string;
    limit: number;
    offset: number;
  }) => Promise<{ ids: string[]; total: number }>;
}
const initialState = {
  lines: new Map<string, HydratedInventoryLine>(),
  loadingByItemId: {},
};

const latestRequests = new Map<string, symbol>();

/** Read-only, organization-scoped inventory cache. */
export const useInventoryStore = create<InventoryStore>((set) => ({
  ...initialState,
  fetchLines: async (params) => {
    const organizationId = useAuthStore.getState().user?.organization.id;
    const request = Symbol();
    latestRequests.set(params.itemId, request);
    const isCurrentRequest = () =>
      organizationId === useAuthStore.getState().user?.organization.id &&
      latestRequests.get(params.itemId) === request;
    set((state) => ({
      loadingByItemId: {
        ...state.loadingByItemId,
        [params.itemId]: { isLoading: true, error: null },
      },
    }));
    try {
      const query = new URLSearchParams({
        itemId: params.itemId,
        limit: String(params.limit),
        offset: String(params.offset),
      });
      const response = await fetchApi(`inventory-lines?${query}`, "GET");
      if (!response.ok) {
        throw new Error(response.error ?? "Unable to load inventory");
      }
      const data = ListInventoryLinesResponseSchema.parse(response.data);
      if (!isCurrentRequest()) {
        return { ids: [], total: 0 };
      }
      set((state) => {
        const lines = new Map(state.lines);
        data.lines.forEach((line) => lines.set(line.id, line));
        return {
          lines,
          loadingByItemId: {
            ...state.loadingByItemId,
            [params.itemId]: { isLoading: false, error: null },
          },
        };
      });
      return { ids: data.lines.map((line) => line.id), total: data.total };
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
      if (isCurrentRequest()) {
        set((state) => ({
          loadingByItemId: {
            ...state.loadingByItemId,
            [params.itemId]: {
              isLoading: false,
              error: "Unable to load inventory. Try refreshing.",
            },
          },
        }));
      }
      return { ids: [], total: 0 };
    }
  },
}));

useAuthStore.subscribe((state, previous) => {
  if (state.user?.organization.id !== previous.user?.organization.id) {
    latestRequests.clear();
    useInventoryStore.setState(initialState);
  }
});
